/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class GlobalFiltersImpl
 * @hideconstructor
 * @classdesc
 */

define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/GlobalFiltersAPI',
	'./Filters',
	'../../../filters/Filters',
	'../../../filters/VisFilterProxy',
	'../../../dataSources/utils/DatasourceUtil',
	'underscore'
], (APIFactory, GlobalFiltersAPI, Filters, FilterCollection, FilterProxy, DatasourceUtil, _) => {
	class GlobalFilters {

		constructor(options) {
			this.dashboard = options.features['API'];
			this.logger = options.features['Logger'];
		}

		_getPageContext() {
			const pageContextService = this.dashboard.getFeature('PageContextService');
			return pageContextService && pageContextService.getPageContext();
		}

		destroy() {
			this.dashboard = null;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [GlobalFiltersAPI]);
			}
			return this.api;
		}

		getFilters(sourceId, scope) {
			const filters = new Filters(this.dashboard, sourceId, scope);
			return filters.getAPI();
		}

		/**
		 * Return the content based on the content Id on the canvas
		 * @param  {id} String the identifier of the content
		 * @return {object} the content object instance
		 */
		_getContent(id) {
			return this.dashboard.getCanvas().getContent(id);
		}

		getFilterList(selectors, widgetId) {
			const slots = widgetId && this._getWidgetSlots(widgetId);
			const filters = new FilterCollection();
			const proxy = new FilterProxy({
				selectors: selectors,
				pageContextItems: this._getPageContextItemsByTableRelation(selectors, widgetId),
				input: filters,
				output: filters,
				logger: this.logger
			});
			proxy.convertFilters(slots, { content: this._getContent(widgetId) });
			return filters.toJSON();
		}

		removeFilters(selectors, transactionToken, options) {
			options = this._applyTransactionToken(transactionToken, options);
			return this._getPageContext().deletePageContextItems(selectors, options);
		}

		clearFilters(selectors, transactionToken) {
			const options = this._applyTransactionToken(transactionToken);
			return this._getPageContext().clearPageContextItemValues(selectors, options);
		}

		_getWidgetSlots(widgetId) {
			const canvas = this.dashboard.getCanvas();
			const widget = canvas.getContent(widgetId);
			if (widget) {
				const visualization = widget.getFeature('Visualization');
				const slots = visualization && visualization.getSlots();
				return slots ? slots.getMappedSlotList() : null;
			}
			return null;
		}

		_getPageContextItemsByTableRelation(selectors, widgetId) {
			const netPageContextItems =  this._getPageContext().getNetPageContextItems(selectors) || [];
			if (!netPageContextItems.length || !selectors.sourceId) {
				return netPageContextItems;
			}

			const dataSource = this.dashboard.getFeature('DataSources').getDataSource(selectors.sourceId);
			const moserDataSources = this.dashboard.getFeature('DataSources.moser');
			let slots = null;
			if (widgetId) {
				const content = this.dashboard.getCanvas().getContent(widgetId);
				if (content) {
					const visualization = content.getFeature('Visualization');
					slots = visualization && visualization.getSlots();
				}
			}

			// Longer term - this filtering should be removed. The query service will ignore filters that don't apply
			// (instead of throwing an error) and return messages indicating that certain filters are being ignored.
			// Shorter term - we may need to compare table references to avoid errors but if there is no table ref,
			// just let it pass through (e.g. for a global calculation) and let the query service handle it.
			const itemIds = slots ? slots.getDataItemList().map(item => item.getColumnId()): [];
			let visTableRefs = DatasourceUtil.getTableRef(dataSource, itemIds);
			const isEmptyTableRef = (aTableRef) => {
				return !aTableRef || _.every(aTableRef, ref => !ref);
			};
			const haveTableJoin = (pageContextItem) => {
				if (isEmptyTableRef(visTableRefs)) {
					return true;
				}
				let pageContextTableRefs = DatasourceUtil.getPageContextItemTableRef(dataSource, pageContextItem);
				return isEmptyTableRef(pageContextTableRefs) || DatasourceUtil.haveTableJoinsInSameDataSource(dataSource, pageContextTableRefs, visTableRefs);
			};
			return netPageContextItems.filter(pageContextItem => {
				// Don't send anything page context entries that tables are no joined, otherwise get error from such as:
				// "code": "XQE-MSR-0008",
				// "details": "XQE-MSR-0008 In module \"newModel\", the following query subjects are not joined: \"Page_1\", \"Page_2\", \"Synch Source 2.xlsx\".\r\n\t
				return DatasourceUtil.mustVerifyJoinTablesInSameDataSource(dataSource, pageContextItem, itemIds, moserDataSources) ? haveTableJoin(pageContextItem) : true;
			});
		}

		_applyTransactionToken(transactionToken, options) {
			if (transactionToken && transactionToken.transactionId) {
				options = options || {};
				options.payloadData = {
					undoRedoTransactionId: transactionToken.transactionId,
					transactionToken: transactionToken
				};
			}
			return options;
		}
	}
	return GlobalFilters;
});
